import API from '@aws-amplify/api';
import debug from 'debug';
import { LOG_TIMING } from 'environment';
import { TCNCard, TCNSwapCard, TCNSwapRef, TCNBrand } from 'types/tcn';

const d = debug('amplify');

export const tcnCardLookup = async (handle: string): Promise<TCNCard> => {
  d('tcnCardLookup', handle);
  if (LOG_TIMING) console.time('lookupCard-' + handle);
  const cacheCrusher = '?_' + new Date().getTime();
  return API.get('tcn', '/cardlookup/' + handle + cacheCrusher, {}).then((res) => {
    d('getApiCard', res);
    if (LOG_TIMING) console.timeEnd('lookupCard-' + handle);
    if (res && res.data) return res.data;
    throw new Error('ERROR_LOADING_CARD');
  });
};

interface TCNCardActivateResult {
  // NOTE: This is just a guess at the field types. Please update depending on what is seen in practice.
  data: 'status updated';
}

export const tcnCardActivate = async (handle: string, data: any = {}): Promise<TCNCardActivateResult> => {
  d('tcnCardActivate', handle);
  if (LOG_TIMING) console.time('activateCard-' + handle);
  return API.post('tcn', '/cardactivate/' + handle, {
    body: data,
  })
    .then((res) => {
      d('tcnCardActivate', res);
      if (LOG_TIMING) console.timeEnd('activateCard-' + handle);
      if (res && res.data) return res.data;
      throw new Error('ERROR_ACTIVATING_CARD');
    })
    .catch((err) => {
      if (err && err.response && err.response.data && err.response.data.error) {
        // Return error with referenceID
        // if (err.response.data.referenceID) throw new Error(err.response.data.error + ' (REF: ' + err.response.data.referenceID.substring(4) + ')');
        // Return normal error.
        throw new Error(err.response.data.error);
      }
      throw err;
    });
};

export const tcnCardActivateDefault = async (handle: string, data: any = {}): Promise<TCNCardActivateResult> => {
  if (!data.hasOwnProperty('signup')) data.signup = false;
  d('tcnCardActivateDefault', handle, data);
  return API.post('tcn', '/activateDefault/' + handle, {
    body: data,
  })
    .then((res) => {
      d('tcnCardActivateDefault', res);
      if (res && res.data) return res.data;
      throw new Error('ERROR_ACTIVATING_CARD');
    })
    .catch((err) => {
      if (err && err.response && err.response.data && err.response.data.error) {
        // Return error with referenceID
        // if (err.response.data.referenceID) throw new Error(err.response.data.error + ' (REF: ' + err.response.data.referenceID.substring(4) + ')');
        // Return normal error.
        throw new Error(err.response.data.error);
      }
      throw err;
    });
};

export const tcnGetCardBalance = async (cardIdentifier: string, pin: string): Promise<TCNCard> => {
  d('tcnGetCardBalance', cardIdentifier, pin);
  return API.post('tcn', '/card/balance', {
    body: {
      cardIdentifier,
      pin,
    },
  })
    .then((res) => {
      d('tcnGetCardBalance', res);
      if (res && res.data) return res.data;
      throw new Error('ERROR_CHECKING_BALANCE');
    })
    .catch((err) => {
      if (err && err.response && err.response.data && err.response.data.error) {
        // Return error with referenceID
        // if (err.response.data.referenceID) throw new Error(err.response.data.error + ' (REF: ' + err.response.data.referenceID.substring(4) + ')');
        // Return normal error.
        throw new Error(err.response.data.error);
      }
      throw err;
    });
};

export interface TCNEGiftVoucherCardActivateResult {
  swap_id: string;
  swap_identifier: string;
  swap_api_request_date: string | null;
  swap_amount: string;
  swap_brand: string;
  swap_status: string;
  swap_fname: string;
  swap_lname: string;
  swap_email: string;
  swap_mobile: string;
  brand_name: string;
  brand_logo: string;
  brand_image: string;
  passcolor: string;
  barcodeflag: string;
  barcodeformat: string;
  brand_description: string;
  brand_voucher_expiry: string | null;
  brand_link: string;
  swap_inventory_code: string;
}

export const tcnEGiftVoucherCardActivate = async (handle: string, data: any = {}): Promise<TCNEGiftVoucherCardActivateResult> => {
  if (!data.hasOwnProperty('signup')) data.signup = false;
  d('tcnEGiftVoucherCardActivate', handle);
  if (LOG_TIMING) console.time('activateEGiftVoucherCard-' + handle);
  return API.post('tcn', '/egiftvoucher/activate/' + handle, {
    body: data,
  })
    .then((res) => {
      d('tcnEGiftVoucherCardActivate', res);
      if (LOG_TIMING) console.timeEnd('activateEGiftVoucherCard-' + handle);
      if (res && res.data) return res.data;
      throw new Error('ERROR_ACTIVATING_CARD');
    })
    .catch((err) => {
      if (err && err.response && err.response.data && err.response.data.error) {
        throw new Error(err.response.data.error);
      }
      throw err;
    });
};

export const tcnCardBrandLookup = async (handle: string) => {
  // d('tcnCardLookup', handle);
  // if (LOG_TIMING) console.time('lookupCard-' + handle);
  // const cacheCrusher = '?_' + new Date().getTime();
  return API.get('tcn', '/cardbrandlookup/' + handle, {})
    .then((res) => {
      d('tcnCardBrandLookup', res);
      if (res && res.data) return res.data;
      throw new Error('ERROR_LOADING_CARD_BRANDS');
    })
    .catch((err) => {
      if (err && err.response && err.response.data && err.response.data.error) {
        throw new Error(err.response.data.error);
      }
      throw err;
    });
};

export const tcnGetSkus = async (handle: string): Promise<TCNBrand[]> => {
  return API.get('tcn', '/getskus/' + handle, {})
    .then((res: any) => {
      if (res && res.data) return res.data;
      throw new Error('ERROR_LOADING_CARD');
    })
    .catch((err) => {
      if (err && err.response && err.response.data && err.response.data.error) {
        throw new Error(err.response.data.error);
      }
      throw err;
    });
};

export const tcnPostConversion = async (handle: string, data: any = {}) => {
  if (!data.hasOwnProperty('signup')) data.signup = false;
  return API.post('tcn', '/postconversion/' + handle, {
    body: data,
  })
    .then((res) => {
      if (res && res.data) return res.data;
      throw new Error('ERROR_ACTIVATING_CARD');
    })
    .catch((err) => {
      if (err && err.response && err.response.data && err.response.data.error) {
        throw new Error(err.response.data.error);
      }
      throw err;
    });
};

export const tcnAddEmailToKlaviyo = async (data: any = {}) => {
  return API.post('support', '/support/klaviyo/newsletter/subscribe', {
    body: data,
  })
    .then((res) => {
      if (res) return res;
      throw new Error('ERROR_ADDING_EMAIL_TO_KLAVIYO');
    })
    .catch((err) => {
      if (err && err.response && err.response.data && err.response.data.error) {
        throw new Error(err.response.data.error);
      }
      throw err;
    });
};

// EGift voucher and Multi-choice cards can be swapped for other gift cards.
// EGift vouchers can be swapped once and the full value on the card is
// moved to the new card.
// Multi-choice cards can be partially swapped. For example a customer
// has a $200 Her card. She chooses to swap it for a $100 JB Hi Fi card.
// The next day she returns to swap it for a $100 Myer card.
//
// In this above example the Her card is the "parent" card and the
// JB Hi-Fi & Myer cards are "child" cards.
//
// The tcnGetSwaps function returns all swaps made against a parent card.
// - `cardIdentifier` is the parent card identifier.
export const tcnGetSwaps = async (cardIdentifier: string): Promise<Array<TCNSwapRef>> => {
  return API.get('tcn', '/getswaps/' + cardIdentifier, {}).then((res) => {
    d('tcnGetSwaps', res);
    if (res && res.data) return res.data;
    throw new Error('ERROR_LOADING_GET_SWAPS');
  });
};

// Given a `swapIdentifier`, the `tcnGetSwap` function will
// return all details of the "child" cards.
// The `swapIdentifer` value is returned from the `tcnGetSwaps` function call.
export const tcnGetSwap = async (swapIdentifier: string): Promise<TCNSwapCard> => {
  return API.get('tcn', '/getswap/' + swapIdentifier, {}).then((res) => {
    d('tcnGetSwap', res);
    if (res && res.data) return res.data;
    throw new Error('ERROR_LOADING_GET_SWAP');
  });
};

export const tcnContactUs = async (data: any = {}) => {
  return API.post('support', '/support/contact-us', {
    body: data,
  })
    .then((res) => {
      if (res) return res;
      throw new Error('ERROR_CONTACT_US');
    })
    .catch((err) => {
      if (err && err.response && err.response.data && err.response.data.error) {
        throw new Error(err.response.data.error);
      }
      throw err;
    });
};

interface ValidatePhysicalCardResponse {
  isValid: boolean;
  data?: {
    cardIdentifier: string;
  };
  error?: any;
}

export const tcnValidatePhysicalCard = async (activationCode: string, lastThreeDigits: string): Promise<ValidatePhysicalCardResponse> => {
  const data = { activationCode, lastThreeDigits };
  return API.post('tcn', '/card/physical/validate', {
    body: data,
  })
    .then((res) => {
      const cardIdentifier = res.data?.card_identifier;
      if (!cardIdentifier && typeof cardIdentifier !== 'string') {
        return {
          isValid: false,
          error: 'card_idenifier not returned',
        };
      }
      return {
        isValid: true,
        data: { cardIdentifier },
      };
    })
    .catch((err) => {
      return {
        isValid: false,
        error: err && err.response && err.response.data && err.response.data.error,
      };
    });
};
